import React, { useReducer, useState } from 'react';
import dayjs from 'dayjs';
import ContentCard from '../../../Common/Card';
import FakePagination from '../../../../../components/Common/UI/FakePagination';
import DocumentsDataPicker from './DataPicker';
import { Button } from '../../../../../components/Common/UI/Button';
import { Input } from '../../../../../components/Common/UI/Input';
import Icons from '../../../../../components/Common/Icons/Icons';
import { accountData, IAccount } from '../../../Layout/Account/Data';
import Account from './Account';
import GuidanceCheckBox from '../../../Common/UI/CheckBox';
import { DocumentTypesEnum, ISearchResultData } from '../Data';
import DataTable from '../../../../../components/Common/UI/DataTable';
import BatchProcessingNotification from '../BatchZip';
import createBatchDownloadBlob from '../utils';

export interface IBatchDownloadState {
  from: string;
  to: string;
  accounts: IAccount[];
  allAccounts: boolean;
  allDocuments: boolean;
  statements: boolean;
  taxes: boolean;
  notices: boolean;
}

interface IDocumentsSearch {
  searchResult: ISearchResultData[];
  handleSearchResult: (payload: ISearchResultData[]) => void;
  handleCompressIntoPDF: () => void;
}

// function getAccountsForCurrentUser(): IAccount[] {
//   const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');
//   const isAuthorized = tempViewers.length > 0;

//   const filteredGroups = isAuthorized
//     ? accountData.filter(el => el.type === 'checking' || el.type === 'savings')
//     : accountData;

//   return filteredGroups
//     .flatMap(el => el.accounts)
//     .map((acc, index) => ({
//       ...acc,
//       id: index.toString(),
//       checked: false,
//     }));
// }
function getAccountsForCurrentUser(): IAccount[] {
  const viewerUpdates = JSON.parse(localStorage.getItem('viewerUpdates') || '[]');
  const tempViewers = JSON.parse(localStorage.getItem('tempViewers') || '[]');

  let viewerAccounts: string[] = [];

  if (viewerUpdates?.length) {
    viewerAccounts = viewerUpdates?.[0]?.selectedAccounts || [];
  } else if (tempViewers?.length) {
    viewerAccounts = tempViewers?.[0]?.selectedAccounts || [];
  }

  const isAuthorized = viewerAccounts.length > 0;

  const filteredAccounts = accountData
    .flatMap(group => group.accounts)
    .filter(acc => {
      if (!isAuthorized) return true;

      return viewerAccounts.includes(acc.number);
    });

  return filteredAccounts.map((acc, index) => ({
    ...acc,
    id: index.toString(),
    checked: false,
  }));
}

const accounts = accountData
  .map(el => el.accounts)
  .flatMap(subArray => subArray)
  .map((el2, index) => ({ ...el2, id: index.toString(), checked: false }));

const currentYear = dayjs().year();
const currentMonth = dayjs().month();
const currentDay = dayjs().day();

const initialState: IBatchDownloadState = {
  from: `${currentMonth} ${currentDay} ${currentYear}`,
  to: `${currentMonth} ${currentDay} ${currentYear}`,
  accounts: getAccountsForCurrentUser(),
  allAccounts: false,
  allDocuments: false,
  notices: false,
  statements: false,
  taxes: false,
};

function DocumentsSearch({ searchResult, handleSearchResult, handleCompressIntoPDF }: IDocumentsSearch) {
  const [selectAllRows, setSelectAllRows] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [state, dispatch] = useReducer(
    (prevState: IBatchDownloadState, action: Partial<IBatchDownloadState>) => ({
      ...prevState,
      ...action,
    }),
    initialState,
  );

  const handleDispatch = (payload: Partial<IBatchDownloadState>) => {
    dispatch(payload);
  };

  const handleCheck = (id: string) => {
    handleDispatch({
      allAccounts: false,
      accounts: state.accounts.map(el => (el.id === id ? { ...el, checked: !el.checked } : el)),
    });
  };

  const handleAllAccounts = (payload: boolean) => {
    handleDispatch({
      allAccounts: payload,
      accounts: state.accounts.map(el => ({ ...el, checked: payload })),
    });
  };

  const handleAllDocuments = (payload: boolean) => {
    handleDispatch({
      allDocuments: payload,
      statements: payload,
      taxes: payload,
      notices: payload,
    });
  };

  const handleOnlyChecked = () => {
    handleDispatch({ accounts: state.accounts.filter(el => el.checked) });
  };

  const handleSearch = () => {
    const selectedAccounts = state.accounts.filter(el => el.checked);

    let resultData: ISearchResultData[] = [];

    if (selectedAccounts.length === state.accounts.length) {
      for (let i = 0; i < 550; i++) {
        const acc = selectedAccounts[i % selectedAccounts.length];

        resultData.push({
          id: `${acc.id}-${i}`,
          account: acc.number,
          checked: false,
          date: dayjs().subtract(i, 'day').format('MMM DD, YYYY'),
          documentType: ['Tax', 'Statement', 'Notice'][i % 3],
          pageCount: ((i % 10) + 1).toString(),
          from: dayjs()
            .subtract(i + 60, 'day')
            .format('MMM DD, YYYY'),
          to: dayjs().subtract(i, 'day').format('MMM DD, YYYY'),
        });
      }
    } else {
      resultData = selectedAccounts.map((acc, idx) => ({
        id: acc.id,
        account: acc.number,
        checked: false,
        date: dayjs().subtract(idx, 'day').format('MMM DD, YYYY'),
        documentType: ['Tax', 'Statement', 'Notice'][idx % 3],
        pageCount: ((idx % 10) + 1).toString(),
        from: dayjs()
          .subtract(idx + 60, 'day')
          .format('MMM DD, YYYY'),
        to: dayjs().subtract(idx, 'day').format('MMM DD, YYYY'),
      }));
    }

    setSelectAllRows(false);
    handleSearchResult(resultData);
  };

  const handleCheckedChange = (id: string, value: boolean) => {
    handleSearchResult(searchResult.map(item => (item.id === id ? { ...item, checked: value } : item)));
  };

  const columns = [
    {
      header: '',
      id: 'checked',
      // eslint-disable-next-line react/no-unstable-nested-components
      cell: ({ row }: any) => (
        <GuidanceCheckBox
          id={`row-${row.original.id}`}
          label=""
          checked={row.original.checked || false}
          onClick={() => {
            const newChecked = !row.original.checked;

            handleCheckedChange(row.original.id, newChecked);
          }}
        />
      ),
      size: 50,
    },
    {
      header: 'account#',
      accessorKey: 'account',
    },
    {
      header: 'Statement date',
      accessorKey: 'date',
    },
    {
      header: 'Document type',
      accessorKey: 'documentType',
    },
    {
      header: 'Page count',
      accessorKey: 'pageCount',
    },
  ];

  const handleSelectAllRows = () => {
    setSelectAllRows(!selectAllRows);
    handleSearchResult(searchResult.map(el => ({ ...el, checked: !selectAllRows })));
  };
  const pageSize = 10;
  const totalDocsSelected = searchResult.filter(el => el.checked).length;
  const totalDocs = searchResult.length;
  const totalPagesSelected = Math.ceil(totalDocsSelected / pageSize);
  const totalPages = Math.ceil(totalDocs / pageSize);

  const handleCompress = async () => {
    handleCompressIntoPDF();
    const selectedCount = Math.min(searchResult.filter(el => el.checked).length, 50);

    return createBatchDownloadBlob(selectedCount);
  };

  return (
    <ContentCard
      title="Search For Documents"
      subTitle="To filter which accounts you want to combine the
      statements into Batch Job, please select those account number(s), document types and date range"
      titleStyle="mb-[10px]"
    >
      <DocumentsDataPicker from={state.from} to={state.to} handleDispatch={handleDispatch} />
      <div>
        <div className="flex items-center gap-[20px] md:justify-end md:my-0 my-[20px]">
          <Button className="underline p-0 text-[14px] text-addition1" variant="link" onClick={handleOnlyChecked}>
            Show Only Selected
          </Button>
          <div className="text-[#898989] text-[14px] flex gap-[5px] items-center">
            <div className="text-addition6">{state.accounts?.filter(el => el.checked).length || 0}</div>
            of
            <div className="text-addition1">{state.accounts?.length || 0}</div>
            Selected
          </div>
        </div>
        <div className="grid xxl:grid-cols-[275px,auto,1fr] gap-[30px]">
          <div>
            <div className="flex gap-[20px] mb-[20px] xxl:justify-between">
              Account Numbers
              <GuidanceCheckBox
                id="selectAll"
                label="Select All"
                checked={state.allAccounts}
                onClick={() => handleAllAccounts(!state.allAccounts)}
              />
            </div>
            <div className="flex xxl:justify-end">
              <Input
                iconPosition="left"
                sizeVariant="md"
                variant="search"
                placeholder="Search"
                className="xxl:w-[200px] xs:w-[300px] w-full"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                icon={<Icons.Search />}
              />
            </div>
          </div>
          <div className="bg-black xxl:w-[1px] w-full xxl:h-full h-[1px]" />
          <div
            className="grid xxl:grid-cols-[repeat(3,auto)]
          sm:grid-cols-[repeat(2,auto)] md:grid-cols-[repeat(3,auto)]
          lg:gap-x-[70px] md:gap-x-[40px] sm:gap-x-[20px] gap-y-[20px] mt-[2px] justify-start items-start"
          >
            {state.accounts.filter(el => el.number.includes(searchQuery.length >= 3 ? searchQuery : '')).length ===
            0 ? (
              <div className="uppercase text-addition1 text-[12px] font-medium opacity-80 cursor-pointer">
                No items selected
              </div>
            ) : (
              state.accounts
                .filter(el => el.number.includes(searchQuery.length >= 3 ? searchQuery : ''))
                .map(el => <Account key={el.id} account={el} handleCheck={handleCheck} />)
            )}
          </div>
        </div>
      </div>

      <div className="mt-[30px] flex justify-end mb-[10px]">
        <FakePagination value="1-5 of 5" />
      </div>
      <div className="grid md:grid-cols-[275px,auto,1fr] md:gap-[30px] gap-[20px]">
        <div className="flex gap-[20px] md:justify-between">
          Documents Types
          <GuidanceCheckBox
            id="selectAllTypes"
            label="Select All"
            checked={state.allDocuments}
            onClick={() => handleAllDocuments(!state.allDocuments)}
          />
        </div>
        <div className="bg-black w-[1px] h-full md:block hidden" />
        <div
          className="grid grid-cols-[repeat(3,auto)]
        md:gap-x-[40px] gap-x-[20px] gap-y-[20px] justify-start items-start"
        >
          {Object.entries(DocumentTypesEnum).map(([key, value]) => (
            <GuidanceCheckBox
              id={key}
              key={key}
              label={value}
              checked={!!state[key as keyof IBatchDownloadState]}
              onClick={() =>
                handleDispatch({
                  [key]: !state[key as keyof IBatchDownloadState],
                  allDocuments: false,
                })
              }
            />
          ))}
        </div>
      </div>

      <div className="flex sm:justify-center mt-[30px] sm:gap-[40px] gap-[20px] flex-wrap">
        <Button
          className="xs:w-[170px] w-full h-[38px] p-0 !bg-[#965BF3] !text-white uppercase hover:opacity-80"
          onClick={() => {
            handleDispatch(initialState);
            setSearchQuery('');
            handleSearchResult([]);
            handleCompressIntoPDF();
          }}
        >
          Reset
        </Button>
        <Button
          className="xs:w-[170px] w-full h-[38px] p-0 !bg-primary !text-white uppercase hover:opacity-80"
          onClick={handleSearch}
        >
          Search
        </Button>
      </div>
      {searchResult.length ? (
        <ContentCard
          title="Search Results"
          subTitle="Please select which statements you want to combine into a ZIP
          by clicking on the check box under the “SELECT ALL” column.
          After you’ve select all the statements,
          click on the Combine into Single PDF or Compress into ZIP button to generate the job."
          titleStyle="mb-[10px]"
          className="mt-[20px]"
        >
          <div
            className="sm:pl-[20px] flex my-[20px]
          sm:items-center sm:gap-[20px] gap-[15px] xs:flex-row flex-col flex-wrap"
          >
            <GuidanceCheckBox
              id="selectAllRows"
              label="Select All"
              checked={selectAllRows}
              onClick={handleSelectAllRows}
            />
            <div className="flex items-center gap-[5px] text-[12px]">
              <div className="text-addition1 min-w-5">{`${totalPagesSelected}/${totalPages}`}</div>
              <div className="text-addition1-50">Pages Selected</div>
            </div>
            <div className="flex items-center gap-[5px] text-[12px]">
              <div className="text-addition1 min-w-5">{`${totalDocsSelected}/${totalDocs}`}</div>
              <div className="text-addition1-50">Documents Selected</div>
            </div>
          </div>
          <DataTable
            responsive
            variant="guidance2"
            columns={columns}
            data={searchResult}
            paginationPosition="bottom"
            paginationVariant="guidance"
            initialState={{ pagination: { pageSize: 10 } }}
            onRow={row => {
              const isChecked = searchResult.find(el => el.id === row.original.id)?.checked || false;

              handleCheckedChange(row.original.id, !isChecked);
            }}
          />
          <div className="flex gap-[10px] flex-wrap sm:justify-center items-center mt-[20px]">
            <Button
              className="px-[20px] py-[3px] xs:h-[32px] h-[38px]
            !bg-[#522E5E] !text-white hover:opacity-80 xs:w-auto w-full"
            >
              <Icons.PDF2 className="[&_path]:fill-white h-[18px]" />
              Combine Into Single PDF
            </Button>
            {/* <Button
              className="px-[20px] py-[3px] xs:h-[32px] h-[38px]
              !bg-primary !text-white hover:opacity-80 xs:w-auto w-full"
              onClick={handleCompressIntoPDF}
              disabled={searchResult.filter((el) => el.checked).length === 0}
            >
              <Icons.ZIP />
              Compress into Zip
            </Button> */}
            <BatchProcessingNotification
              onCompress={handleCompress}
              disabled={searchResult.filter(el => el.checked).length === 0}
            />
          </div>
        </ContentCard>
      ) : null}

      {searchQuery.length && searchQuery.length < 3 ? (
        <div className="text-[#F25B2A] font-bold text-center mt-[20px]">Select at least 3 characters for search</div>
      ) : null}
    </ContentCard>
  );
}

export default DocumentsSearch;
